/**
 * Lazy booking widget panes.
 *
 * Panes that are not active on page load (Eat & Drink, Gather, Spa) are server-rendered
 * inside <template class="js-booking-pane-template"> (see views/partial/booking/widget.twig),
 * so their markup is not part of the live DOM until the visitor needs it. ensurePane()
 * moves the markup into the document the first time the pane is shown and fires
 * PANE_READY_EVENT on the pane, so the tab modules can run the same setup they run
 * on DOMContentLoaded for server-rendered markup.
 *
 * This module keeps no state (everything lives in the DOM), so it is safe to import from
 * separate bundles (e.g. blocks/optional/brand-venues).
 */

export const PANE_READY_EVENT = 'booking:pane-ready';

const TEMPLATE_SELECTOR = 'template.js-booking-pane-template';

/**
 * Fields that third-party tags fill by id shortly after page load (GTM fills
 * #session-utm-* in the Gather form). While the form sits in a <template> those ids are
 * not in the document, so a hidden stand-in with the same id (no name, outside any form)
 * keeps receiving the value, which is copied to the real field when the pane is rendered.
 */
const PROXY_FIELDS_SELECTOR = 'input[id^="session-utm-"]';
const PROXY_ATTRIBUTE = 'data-booking-pane-proxy';

function getTemplate(pane) {
  return pane ? pane.querySelector(`:scope > ${TEMPLATE_SELECTOR}`) : null;
}

export function isPaneRendered(pane) {
  return !!pane && !getTemplate(pane);
}

/**
 * Create the hidden stand-ins for every lazy pane (called once on load).
 * @param {ParentNode} root
 */
export function prepareProxyFields(root = document) {
  root.querySelectorAll(TEMPLATE_SELECTOR).forEach((template) => {
    template.content.querySelectorAll(PROXY_FIELDS_SELECTOR).forEach((field) => {
      if (!field.id || document.getElementById(field.id)) {
        return;
      }

      const proxy = document.createElement('input');
      proxy.type = 'hidden';
      proxy.id = field.id;
      proxy.setAttribute(PROXY_ATTRIBUTE, '');
      template.before(proxy);
    });
  });
}

function takeProxyValues(pane) {
  const values = new Map();

  pane.querySelectorAll(`[${PROXY_ATTRIBUTE}]`).forEach((proxy) => {
    if (proxy.value) {
      values.set(proxy.id, proxy.value);
    }

    proxy.remove();
  });

  return values;
}

function applyProxyValues(values) {
  values.forEach((value, id) => {
    const field = document.getElementById(id);

    if (field) {
      field.value = value;
    }
  });
}

function fetchCf7Schema(id) {
  const { api, schemas } = window.wpcf7;

  if (!id || !api || !api.root || !schemas || schemas.get(id)) {
    return;
  }

  // Same URL CF7 builds for the schema request it sends on DOMContentLoaded.
  const namespace = (api.namespace || 'contact-form-7/v1').replace(/^\/|\/$/g, '');
  let path = `${namespace}/contact-forms/${id}/feedback/schema`;

  if (api.root.indexOf('?') !== -1) {
    path = path.replace('?', '&');
  }

  window.fetch(`${api.root}${path}`, { headers: { Accept: 'application/json, */*;q=0.1' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((schema) => {
      if (schema) {
        schemas.set(id, schema);
      }
    })
    .catch(() => {});
}

/**
 * Initialise Contact Form 7 forms that were added after CF7 ran its own
 * DOMContentLoaded pass. When CF7 has not run yet (Perfmatters delays it until the first
 * interaction), its own pass picks the forms up, so nothing is done here.
 * @param {ParentNode} root
 */
function initCf7Forms(root) {
  if (!window.wpcf7 || typeof window.wpcf7.init !== 'function') {
    return;
  }

  root.querySelectorAll('.wpcf7 > form').forEach((form) => {
    if (form.wpcf7) {
      return;
    }

    window.wpcf7.init(form);
    form.closest('.wpcf7').classList.replace('no-js', 'js');
    fetchCf7Schema(form.wpcf7 && form.wpcf7.id);
  });
}

/**
 * Render a lazy pane (no-op when it is already rendered).
 * @param {Element|null} pane
 * @returns {boolean} true when the pane has just been rendered
 */
export function ensurePane(pane) {
  const template = getTemplate(pane);

  if (!template) {
    return false;
  }

  const proxyValues = takeProxyValues(pane);
  template.replaceWith(document.importNode(template.content, true));

  initCf7Forms(pane);
  pane.dispatchEvent(new CustomEvent(PANE_READY_EVENT, { bubbles: true }));

  // After the ready handlers, which may reset the form (see GatherTab).
  applyProxyValues(proxyValues);

  return true;
}

/**
 * Render the pane controlled by a booking tab button.
 * @param {Element|null} tab
 */
export function ensurePaneForTab(tab) {
  const target = tab && tab.getAttribute('data-bs-target');

  if (target) {
    ensurePane(document.querySelector(target));
  }
}
